import React from 'react';
import { LeftToRightListBulletIcon } from 'hugeicons-react';
import { useResourceContext } from '../../contexts/ResourceContext';
import { ResourceListItem } from './ResourceListItem';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '../ui/dropdown-menu';
import '../../Styles/Layout/Resources/ResourcesListOverlay.css';
import { useAttachmentContext } from '../../contexts/AttachmentContext';
import { getFileAttachmentIcon } from '../../Data/IconData';

export const ResourcesListOverlay: React.FC = () => {
  const {
    currentChatResources: resources,
    isResourcesListVisible,
    setIsResourcesListVisible,
  } = useResourceContext();
  const {
    textFiles,
    activeTextAttachmentFileId,
    isTextAttachmentViewOpen,
    handleFileCardClick,
  } = useAttachmentContext();

  const uniqueResources = Object.values(
    resources.reduce((acc, resource) => {
      if (!acc[resource.title] || (resource.version ?? 0) > (acc[resource.title].version ?? 0)) {
        acc[resource.title] = resource;
      }
      return acc;
    }, {} as Record<string, typeof resources[number]>)
  );

  if (uniqueResources.length + textFiles.length <= 1) return null;

  const getResourceVersions = () => {
    const versions: Record<string, typeof resources> = {};
    resources.forEach(r => {
      if (!versions[r.title]) versions[r.title] = [];
      versions[r.title].push(r);
    });
    return versions;
  };
  const resourceVersions = getResourceVersions();

  return (
    <DropdownMenu open={isResourcesListVisible} onOpenChange={setIsResourcesListVisible}>
      <DropdownMenuTrigger asChild>
        <button
          id="resource-dropdown-toggle-button"
          type="button"
          aria-label="Toggle resources list"
          className={`
            bg-none border-none cursor-pointer p-1 flex rounded-md
            transition-colors
            ${isResourcesListVisible ? 'bg-[var(--background-dark)]' : ''}
            hover:bg-[var(--background-dark)]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]
          `}
        >
          <LeftToRightListBulletIcon size={20} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className="resources-list-dropdown"
        sideOffset={8}
      >
        <div className="resources-list-dropdown-inner">
          {textFiles.length > 0 && (
            <div className="resources-list-dropdown-header">Next message</div>
          )}
          {textFiles.map(file => (
            <button
              key={file.id}
              type="button"
              className={`rli${isTextAttachmentViewOpen && activeTextAttachmentFileId === file.id ? ' rli--active' : ''}`}
              disabled={!file.isLoaded}
              onClick={() => {
                handleFileCardClick(file);
                setIsResourcesListVisible(false);
              }}
            >
              <span className="rli-icon">{getFileAttachmentIcon(file.type)}</span>
              <span className="rli-title" title={file.name}>{file.name}</span>
              <span className="rli-status">
                {file.id.startsWith('create-') ? 'Draft' : file.isLoaded ? 'Attached' : 'Uploading'}
              </span>
            </button>
          ))}
          {uniqueResources.length > 0 && (
            <div className="resources-list-dropdown-header">This chat</div>
          )}
          {Object.entries(resourceVersions).map(([title, versions]) => {
            const latest = versions.reduce((a, b) =>
              (b.version ?? 0) > (a.version ?? 0) ? b : a
            );
            return (
              <ResourceListItem
                key={latest.id || title}
                resource={latest}
              />
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
